import React, {useMemo, useState} from 'react';
import {ButtonPriority} from '@wix/wix-ui-tpa';
import {Button, Dropdown, Text, TextField, TextPriority} from '@wix/wix-ui-tpa/cssVars';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {Skeleton} from '../../../../../../../../common/components/Skeleton/Skeleton';
import {SkeletonSection} from '../../../../../../../../common/components/Skeleton/SkeletonSection/SkeletonSection';
import {SkeletonRow} from '../../../../../../../../common/components/Skeleton/SkeletonRow/SkeletonRow';
import {SkeletonLineWrapper} from '../../../../../../../../common/components/Skeleton/SkeletonLine/SkeletonLineWrapper';
import s from './InlineAddressForm.scss';
import {AddressModel} from '../../../../../../../../domain/models/Cart/Address.model';
import {CountriesData} from '../loadCountriesData';

export enum InlineAddressFormDataHook {
  Root = 'InlineAddressForm.Root',
  Title = 'InlineAddressForm.Title',
  Loader = 'InlineAddressForm.Loader',
  CountryDropdown = 'InlineAddressForm.CountryDropdown',
  SubdivisionDropdown = 'InlineAddressForm.SubdivisionDropdown',
  PostalCodeField = 'InlineAddressForm.PostalCodeField',
  Action = 'InlineAddressForm.Action',
}

type InlineAddressFormProps = {
  onSubmit: (address: {country: string; subdivision?: string; postalCode?: string}) => void;
  isLoadingCountries: boolean;
  countryData?: CountriesData;
  address: AddressModel;
};

export const InlineAddressForm = ({onSubmit, isLoadingCountries, countryData, address}: InlineAddressFormProps) => {
  const {t} = useTranslation();
  const [selectedCountryCode, setSelectedCountryCode] = useState(address.country);
  const [selectedSubdivisionCode, setSelectedSubdivisionCode] = useState(address.subdivision);
  const [postalCode, setPostalCode] = useState(address.postalCode);

  const subdivisionsData = useMemo(
    () => countryData?.getSubdivisionsForCountry(selectedCountryCode),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoadingCountries, selectedCountryCode]
  );

  const handleCountryChange = (countryCode: string) => {
    setSelectedCountryCode(countryCode);
    setSelectedSubdivisionCode(countryData?.getSubdivisionsForCountry(countryCode)?.options[0]?.id);
    setPostalCode('');
  };

  return (
    <div className={s.root} data-hook={InlineAddressFormDataHook.Root}>
      <Text priority={TextPriority.primary} data-hook={InlineAddressFormDataHook.Title}>
        {t('cart.change_region_modal.title')}
      </Text>

      {isLoadingCountries ? (
        <Skeleton dataHook={InlineAddressFormDataHook.Loader}>
          <SkeletonSection>
            <SkeletonRow>
              <SkeletonLineWrapper />
            </SkeletonRow>
            <SkeletonRow>
              <SkeletonLineWrapper />
            </SkeletonRow>
            <SkeletonRow>
              <SkeletonLineWrapper />
            </SkeletonRow>
          </SkeletonSection>
        </Skeleton>
      ) : (
        <div className={s.fields}>
          <Dropdown
            data-hook={InlineAddressFormDataHook.CountryDropdown}
            initialSelectedId={address.country}
            options={countryData?.countries}
            upgrade={true}
            label={t('cart.change_region_modal.country_label')}
            onChange={({id}) => handleCountryChange(id)}
          />

          {subdivisionsData && (
            <Dropdown
              key={selectedCountryCode}
              data-hook={InlineAddressFormDataHook.SubdivisionDropdown}
              selectedId={selectedSubdivisionCode}
              options={subdivisionsData.options}
              upgrade={true}
              label={subdivisionsData.label}
              onChange={({id}) => setSelectedSubdivisionCode(id)}
            />
          )}

          <TextField
            data-hook={InlineAddressFormDataHook.PostalCodeField}
            className={s.textField}
            label={t('cart.change_region_modal.zip_label')}
            value={postalCode}
            maxLength={50}
            onChange={(e) => setPostalCode(e.target.value)}
          />
        </div>
      )}

      <Button
        priority={ButtonPriority.basic}
        data-hook={InlineAddressFormDataHook.Action}
        fullWidth
        upgrade={true}
        disabled={isLoadingCountries || !selectedCountryCode}
        onClick={() => onSubmit({country: selectedCountryCode, subdivision: selectedSubdivisionCode, postalCode})}>
        {t('cart.change_region_modal.update')}
      </Button>
    </div>
  );
};
